import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Box,
  type ButtonProps,
} from '@mui/material';
import { GridLogicOperator, type GridFilterModel } from '@mui/x-data-grid';
import { ProjectDesignerRoleTags } from '@nohara-dxdev/mt-common';
import { permission } from '@nohara-dxdev/oidc-core';
import { OidcProtectedComponent, useOidc, useScope } from '@nohara-dxdev/react-oidc';
import { useState, useMemo, useLayoutEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ItemEntity, ItemTemplateEntityWithSchema } from '@/types/api';

import { CreateItemDto, useCreateItem } from '../api/create-item';
import {
  ItemFilterValuesProvider,
  ItemTemplateFilterValuesProvider,
  useItemFilterValues,
} from '../providers';

import { ItemForm } from './item-form';
import { SelectItemTemplateModal } from './select-item-template-modal';

const createItemPermissions = [permission.item.create];

interface CreateItemFormData extends CreateItemDto {
  selectedTemplate?: ItemTemplateEntityWithSchema;
}

export interface CreateItemDialogProps {
  /** Called with the created item after a successful create (MT1-1415: lets
   * the Load List graph seed the new item into its node set). */
  onCreated?: (item: ItemEntity) => void;
  /** Overrides for the trigger button so embedding surfaces (e.g. the graph
   * control panel) can match their local button sizing. */
  buttonProps?: ButtonProps & { 'data-testid'?: string };
}

export const CreateItemDialog = ({ onCreated, buttonProps }: CreateItemDialogProps = {}) => {
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const { ability } = useOidc();
  const { scope } = useScope();

  const { t } = useTranslation();
  const { mutate: createItem, isPending } = useCreateItem();

  // Form to handle template selection and item creation
  const form = useForm<CreateItemFormData>({
    defaultValues: {
      selectedTemplate: undefined,
    },
  });

  const selectedTemplate = form.watch('selectedTemplate');

  const resetForm = () => {
    form.reset();
  };

  const handleCreate = (data: CreateItemDto) => {
    if (!selectedTemplate) return;

    createItem(
      {
        ...data,
        itemTemplateId: selectedTemplate.id,
      },
      {
        onSuccess: (createdItem) => {
          resetForm();
          onCreated?.(createdItem);
        },
      },
    );
  };

  const handleOpenTemplateModal = () => {
    setIsTemplateModalOpen(true);
  };

  const handleTemplateModalClose = () => {
    setIsTemplateModalOpen(false);
  };

  const handleFormDialogClose = () => {
    setIsFormDialogOpen(false);
    resetForm();
  };

  // Define filter values for item template selection
  // This can be customized based on the context of item creation
  const projectRoleTagsFilter = useMemo(() => {
    const allowedProjectRoleTags = Object.values(ProjectDesignerRoleTags).filter((tag) =>
      ability?.has(
        { action: 'update', subject: 'item', scope },
        { itemTemplate: { projectRoleTags: [tag] } },
      ),
    );
    if (allowedProjectRoleTags.length === Object.values(ProjectDesignerRoleTags).length) {
      return [];
    }
    return allowedProjectRoleTags;
  }, [ability, scope]);

  useLayoutEffect(() => {
    if (selectedTemplate) {
      setIsFormDialogOpen(true);
    }
  }, [selectedTemplate]);

  return (
    <>
      <OidcProtectedComponent requireAll={createItemPermissions}>
        <Button
          variant="contained"
          color="primary"
          {...buttonProps}
          onClick={handleOpenTemplateModal}
        >
          {t('items.create.button')}
        </Button>
      </OidcProtectedComponent>

      <FormProvider {...form}>
        <ItemTemplateFilterValuesProvider
          filterValues={{
            advancedFilterModel:
              projectRoleTagsFilter.length > 0
                ? {
                    conditions: [
                      {
                        item: {
                          field: 'projectRoleTags',
                          operator: 'isAnyOf',
                          value: projectRoleTagsFilter as string[],
                        },
                      },
                    ],
                  }
                : undefined,
          }}
        >
          {/* Template Selection Modal */}
          <SelectItemTemplateModal
            name="selectedTemplate"
            open={isTemplateModalOpen}
            onClose={handleTemplateModalClose}
            multiValue={false}
            title={t('items.create.selectTemplateTitle')}
          />

          {/* Item Form Dialog (shown after template selection) */}
          {selectedTemplate && (
            <Dialog open={isFormDialogOpen} onClose={handleFormDialogClose} maxWidth="md" fullWidth>
              <DialogTitle>{t('items.create.title')}</DialogTitle>
              <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                  <Box>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        setIsFormDialogOpen(false);
                        setIsTemplateModalOpen(true);
                      }}
                      disabled={isPending}
                    >
                      {`${t('items.create.template')}: ${selectedTemplate.name}`}
                    </Button>
                  </Box>

                  <ItemFormWithMergedFilters
                    selectedTemplate={selectedTemplate}
                    isPending={isPending}
                    handleCreate={handleCreate}
                    projectRoleTagsFilter={projectRoleTagsFilter}
                  />
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleFormDialogClose} disabled={isPending}>
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  form="create-item-form"
                  variant="contained"
                  color="primary"
                  disabled={isPending}
                >
                  {t('common.submit')}
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </ItemTemplateFilterValuesProvider>
      </FormProvider>
    </>
  );
};

// Component to merge filterModel with parent context
const ItemFormWithMergedFilters = ({
  selectedTemplate,
  isPending,
  handleCreate,
  projectRoleTagsFilter,
}: {
  selectedTemplate: ItemTemplateEntityWithSchema;
  isPending: boolean;
  handleCreate: (data: CreateItemDto) => void;
  projectRoleTagsFilter: ProjectDesignerRoleTags[];
}) => {
  const parentFilterValues = useItemFilterValues();

  // Manually merge parent filterModel with projectRoleTags filter
  const mergedFilterModel = useMemo<GridFilterModel[] | undefined>(() => {
    const parentFilterModels = parentFilterValues.filterModel ?? [];
    const projectRoleTagsItems =
      projectRoleTagsFilter.length > 0
        ? [
            {
              field: 'projectRoleTags',
              operator: 'isAnyOf' as const,
              value: projectRoleTagsFilter as string[],
            },
          ]
        : [];

    // If no filters to merge, return undefined
    if (parentFilterModels.length === 0 && projectRoleTagsItems.length === 0) {
      return undefined;
    }

    // Start with parent filterModels
    const mergedFilterModels: GridFilterModel[] = [...parentFilterModels];

    // Add projectRoleTags filter as a new filterModel in the array
    if (projectRoleTagsItems.length > 0) {
      mergedFilterModels.push({
        items: projectRoleTagsItems,
        logicOperator: GridLogicOperator.And,
      });
    }

    return mergedFilterModels.length > 0 ? mergedFilterModels : undefined;
  }, [parentFilterValues, projectRoleTagsFilter]);

  return (
    <ItemFilterValuesProvider
      filterValues={{
        ...parentFilterValues,
        filterModel: mergedFilterModel,
      }}
    >
      <ItemForm
        template={selectedTemplate}
        disabled={isPending}
        onSubmit={handleCreate}
        formId="create-item-form"
      />
    </ItemFilterValuesProvider>
  );
};
